import { useEffect, useState } from 'react';

import { fetchPriceTierUnitsWithRetry } from './api';

/**
 * Stable key for a booking slot so a new Date instance with the same
 * instant does not cancel an in-flight units fetch.
 */
export const priceTierSlotKey = slotStart => {
  if (!slotStart) {
    return '';
  }
  if (slotStart instanceof Date) {
    return Number.isNaN(slotStart.getTime()) ? '' : slotStart.toISOString();
  }
  if (typeof slotStart === 'object' && slotStart.date) {
    return priceTierSlotKey(slotStart.date);
  }
  return typeof slotStart === 'string' ? slotStart : '';
};

export const unitsKnownForSlot = ({ unitsStatus, unitsSlotKey, slotStart } = {}) =>
  unitsStatus === 'ready' && unitsSlotKey === priceTierSlotKey(slotStart);

/**
 * Shared listing + checkout units fetch: 3 attempts (300ms / 800ms
 * backoff). A failed fetch must not be treated as {} — that makes a
 * sold-out tier look available. Counts only apply to the slot they
 * were fetched for; a slot change is "unknown" until the new response.
 */
export const usePriceTierUnits = ({ listingId, slotStart, enabled, refreshKey } = {}) => {
  const slotKey = priceTierSlotKey(slotStart);
  const [unitsSoldByTierId, setUnitsSoldByTierId] = useState({});
  const [unitsStatus, setUnitsStatus] = useState(enabled ? 'loading' : 'ready');
  const [unitsSlotKey, setUnitsSlotKey] = useState(null);

  useEffect(() => {
    if (!enabled || !listingId) {
      setUnitsSoldByTierId({});
      setUnitsStatus('ready');
      setUnitsSlotKey(slotKey);
      return undefined;
    }
    let cancelled = false;
    fetchPriceTierUnitsWithRetry(listingId, slotStart)
      .then(result => {
        if (!cancelled) {
          setUnitsSoldByTierId(result.unitsSoldByTierId || {});
          setUnitsStatus('ready');
          setUnitsSlotKey(slotKey);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUnitsSoldByTierId(current => (unitsSlotKey === slotKey ? current : {}));
          setUnitsStatus('error');
          setUnitsSlotKey(slotKey);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [listingId, enabled, slotKey, refreshKey]);

  const unitsMatchSlot = unitsSlotKey === slotKey;
  return {
    unitsSoldByTierId: unitsMatchSlot ? unitsSoldByTierId : {},
    unitsStatus: unitsMatchSlot ? unitsStatus : enabled ? 'loading' : 'ready',
    unitsAvailabilityKnown: unitsKnownForSlot({ unitsStatus, unitsSlotKey, slotStart }),
  };
};
